<script setup>
import { ref, computed, onMounted } from 'vue';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Integration from './Integration.vue';
import Spinner from 'shared/components/Spinner.vue';
import TiktokLeadSettingsList from './TiktokLeadSettingsList.vue';

const { t } = useI18n();
const store = useStore();
const integrationLoaded = ref(false);

const integration = useFunctionGetter('integrations/getIntegration', 'tiktok_lead_ads');

const webhookUrl = computed(() => {
  return `${window.location.origin}/webhooks/tiktok`;
});

const copyWebhookUrl = () => {
  navigator.clipboard.writeText(webhookUrl.value);
  useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.WEBHOOK_COPIED'));
};

const initializeTiktokIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeTiktokIntegration();
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto mx-auto w-full max-w-6xl">
    <div v-if="integrationLoaded">
      <Integration
        :integration-id="integration.id || 'tiktok_lead_ads'"
        :integration-logo="integration.logo || 'tiktok.png'"
        :integration-name="integration.name || t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.TITLE')"
        :integration-description="integration.description || t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.DESCRIPTION')"
        :integration-enabled="true"
        integration-action=""
      >
        <template #settings>
          <!-- Webhook Configuration Box -->
          <div class="p-6 bg-n-alpha-1 border-t border-n-weak">
            <div class="p-4 bg-n-solid-2 border border-n-weak rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div class="flex items-start gap-3 min-w-0">
                <div class="p-2 rounded-lg bg-b-50 text-b-600 flex-shrink-0">
                  <i class="i-lucide-webhook text-xl" />
                </div>
                <div class="min-w-0">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.WEBHOOK_BOX_TITLE') }}
                  </h4>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.WEBHOOK_BOX_DESCRIPTION') }}
                  </p>
                  <div class="flex items-center gap-2 mt-2">
                    <code class="px-2.5 py-1 text-xs font-mono bg-n-slate-3 text-n-slate-12 rounded border border-n-weak select-all truncate max-w-lg">
                      {{ webhookUrl }}
                    </code>
                    <Button
                      xs
                      faded
                      slate
                      icon="i-lucide-copy"
                      :label="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.COPY_URL')"
                      @click="copyWebhookUrl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mappings List Component -->
          <TiktokLeadSettingsList />
        </template>
      </Integration>
    </div>
    <div v-else class="flex items-center justify-center flex-1 py-20">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
